// Sea passages (GDD §4, taxgw-gxwg): canals and straits joining two seas that the 0.25° land mask closes (a canal or a
// narrow strait is thinner than a mask cell). The sea-lane grid (sim/src/pathing/sea-lanes.ts) is the strict mask — a
// cell is water only where the mask says so — with each passage carved open along its polyline, so a ship never sails
// over land outside a listed waterway. Every passage runs from open water to open water, so both of its ends are mask
// water. A port's own way out to sea is its harbour channel (harbours.ts), not a passage. The Great Lakes are land in
// the mask, so no seaway leads to them.

import seaPassagesJson from '../data/sea-passages.json'
import { ContentValidationError } from './errors'
import { LAND_MASK } from './geo'

export interface SeaPassage {
  readonly name: string
  /** [lat, lon] in degrees, from open water on one side to open water on the other. */
  readonly points: readonly (readonly [number, number])[]
}

/**
 * Every passage, from content/data/sea-passages.json: the one curated list the sim's sea grid carves and the harbour
 * bake (tools/data/harbours.mjs) carves to tell ocean ports from inland-water ones (taxgw-5jc2).
 */
export const SEA_PASSAGES: readonly SeaPassage[] = seaPassagesJson.passages.map((raw): SeaPassage => {
  const points = raw.points.map((point): readonly [number, number] => {
    const [lat, lon] = point
    if (point.length !== 2 || lat === undefined || lon === undefined || !Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) {
      throw new ContentValidationError(`sea passage ${raw.name} has an invalid point`)
    }
    return Object.freeze([lat, lon] as const)
  })
  return Object.freeze({ name: raw.name, points: Object.freeze(points) })
})

export function validateSeaPassages(): void {
  for (const passage of SEA_PASSAGES) {
    const first = passage.points[0]
    const last = passage.points[passage.points.length - 1]
    if (first === undefined || last === undefined || passage.points.length < 2) throw new ContentValidationError(`sea passage ${passage.name} needs two points`)
    for (const [lat, lon] of [first, last]) {
      if (LAND_MASK.isLand(lat, lon)) throw new ContentValidationError(`sea passage ${passage.name} ends on land at (${lat}, ${lon})`)
    }
  }
}
